import classNames from 'classnames'
import {gsap} from 'gsap'
import * as React from 'react'

import {utils} from '@/common/config'
import {makeEditable, tailwindConfig} from '@/common/utils'

function fetchTableOfContent(richtext) {
  if (richtext) {
    return richtext.content.reduce((all, current) => {
      if (current.type === 'heading' && current.attrs) {
        const heading =
          Array.isArray(current.content) && current.content.map(({text}) => text).join('')
        if (current?.attrs?.level === 2) {
          all.push({heading, items: []})
        }
      }
      return all
    }, [])
  }
  return []
}

const TableOfContent = ({richtext, _editable}) => {
  const tableOfContent = fetchTableOfContent(richtext)
  const cursorRef = React.useRef()
  const [active, setActive] = React.useState(-1)

  React.useEffect(() => {
    const richtextHeadings = tableOfContent.map(({heading}) =>
      document.getElementById(`${utils.toKebabCase(heading)}`)
    )
    if (richtextHeadings.length > 0) {
      const mm = gsap.matchMedia()
      mm.add(`(min-width: ${tailwindConfig.theme.screens['md']})`, () => {
        richtextHeadings.forEach((heading, i) => {
          const link = gsap.utils.toArray('.link')[i]
          let prev = i - 1
          gsap.to(link, {
            scrollTrigger: {
              trigger: heading,
              start: 'top 30%',
              end: 'bottom 30%',
              fastScrollEnd: 3000,
              scrub: true,
              onEnter: () => {
                setActive(i)
              },
              onLeaveBack: () => {
                setActive(prev)
              },
            },
          })
        })
      })
      return () => mm.revert()
    }
  })

  React.useEffect(() => {
    const links = gsap.utils.toArray('.link')

    if (active >= 0) {
      gsap.to(cursorRef.current, {
        y: links[active].offsetTop - 30,
        ease: 'linear',
        duration: 0.2,
      })
    }
  }, [active])

  const handleClick = (event) => {
    const {dataset} = event.currentTarget
    const {hash} = dataset
    const element = document.getElementById(hash.replace('#', ''))
    if (element) {
      element.scrollIntoView({behavior: 'smooth'})
      event.preventDefault()
    }
  }

  return (
    <>
      {tableOfContent.length > 0 && (
        <div
          {...makeEditable(_editable)}
          className='body-m-bold relative flex gap-4 rounded-2xl bg-yellow-200 p-6'
        >
          <div className='relative pr-4'>
            <div className='absolute top-0 left-1 z-0 h-full w-[1px] bg-purple-300' />
            <div ref={cursorRef} className='relative z-10 h-13 w-2 rounded-[30px] bg-violet-400' />
          </div>
          <ol className='space-y-6'>
            {tableOfContent.map(({items, heading}, index) => {
              const hash = `#${utils.toKebabCase(heading)}`
              return (
                <li key={index}>
                  <a
                    className={classNames(
                      'link focus:text-purple-900 active:text-purple-900',
                      {'text-purple-900': active === index},
                      {'text-purple-300': active !== index}
                    )}
                    href={hash}
                    data-hash={hash}
                    onClick={handleClick}
                  >
                    {index + 1} - {heading}
                  </a>

                  <ol>
                    {items.map(({heading}, index) => {
                      const hash = `#${utils.toKebabCase(heading)}`
                      return (
                        <li key={index}>
                          <a
                            className='text-purple-300 focus:text-purple-900 active:text-purple-900'
                            href={hash}
                            data-hash={hash}
                            onClick={handleClick}
                          >
                            {heading}
                          </a>
                        </li>
                      )
                    })}
                  </ol>
                </li>
              )
            })}
          </ol>
        </div>
      )}
    </>
  )
}

export default TableOfContent
